import React, { useMemo } from "react";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCheckoutCart } from "../hooks/use-checkout-cart";

interface VendorCharge {
  vendorId: string;
  vendorName: string;
  vendorLogo?: string;
  total: number;
}

/**
 * Sepette birden fazla satıcı varken, bankanın 3D ekranında yalnızca
 * ilk çekimin göründüğünü ve onayın kalan çekimleri de kapsadığını anlatır.
 */
const useMultiVendorCharges = () => {
  const { vendorGroups, items, formatPrice, totalPrice } = useCheckoutCart();

  const charges = useMemo((): VendorCharge[] => {
    const selectedItems = items.filter((item) => item.isSelected !== false);

    return (vendorGroups ?? [])
      .map((group) => ({
        vendorId: group.vendorId as string,
        vendorName: String(group.vendorName ?? ""),
        vendorLogo: group.vendorLogo as string | undefined,
        total: typeof group.total === "number" ? group.total : 0,
      }))
      .filter((group) =>
        selectedItems.some((item) => item.vendor?.id === group.vendorId),
      );
  }, [vendorGroups, items]);

  const formatAmount = (price: number) => {
    if (typeof price !== "number" || Number.isNaN(price)) {
      return formatPrice(0);
    }
    return formatPrice(price);
  };

  return {
    isMultiVendor: charges.length > 1,
    charges,
    formatAmount,
    totalLabel: formatAmount(totalPrice),
  };
};

const noticeClassName =
  "rounded-xl border border-[var(--colors-border-border-brand)] bg-[var(--colors-background-bg-brand-50)]";

const noticeTextClassName = "text-text-brand dark:text-text-brand";

export const MultiVendorPaymentNotice: React.FC = () => {
  const { t } = useTranslation();
  const { isMultiVendor, charges, formatAmount, totalLabel } =
    useMultiVendorCharges();

  if (!isMultiVendor) {
    return null;
  }

  return (
    <div role="note" className={`${noticeClassName} px-3 py-3 md:px-3.5`}>
      <div className="flex gap-2.5">
        <Info
          className={`mt-0.5 h-4 w-4 shrink-0 ${noticeTextClassName}`}
          aria-hidden
        />

        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-xs md:text-sm font-semibold leading-5 text-text-primary dark:text-text-primary">
            {t("checkout.multiVendorPayment.title")}
          </p>

          <ul className="space-y-1.5 rounded-lg border border-border-secondary bg-bg-primary px-2.5 py-2 dark:border-border-secondary dark:bg-bg-primary">
            {charges.map((charge) => (
              <li
                key={charge.vendorId}
                className="flex items-center justify-between gap-3"
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  {charge.vendorLogo ? (
                    <img
                      src={charge.vendorLogo}
                      alt=""
                      className="h-4 w-4 shrink-0 object-contain"
                    />
                  ) : null}
                  <span className="truncate text-[11px] md:text-xs font-medium text-text-primary dark:text-text-primary">
                    {charge.vendorName}
                  </span>
                </span>
                <span className="shrink-0 text-[11px] md:text-xs font-semibold text-text-primary dark:text-text-primary">
                  {formatAmount(charge.total)}
                </span>
              </li>
            ))}

            <li className="flex items-center justify-between gap-3 border-t border-border-secondary pt-1.5 dark:border-border-secondary">
              <span
                className={`text-[11px] md:text-xs font-semibold ${noticeTextClassName}`}
              >
                {t("checkout.multiVendorPayment.chargedTotal")}
              </span>
              <span
                className={`shrink-0 text-xs md:text-sm font-bold ${noticeTextClassName}`}
              >
                {totalLabel}
              </span>
            </li>
          </ul>

          <p className="text-[11px] md:text-xs font-medium leading-4 md:leading-5 text-text-secondary dark:text-text-secondary">
            {t("checkout.multiVendorPayment.approvalNote")}
          </p>
        </div>
      </div>
    </div>
  );
};
